/**
 * 워크스페이스(CatchMomentsWorkspace) React Query 키.
 *
 * ## ⚠️ 만료 체크는 캐시가 없으면 읽기가 폭증한다
 * `AuthGuard`가 네비게이션마다 `checkUserSessionNow` → `getCheckedWorkspaceList`(전 컬렉션 +
 * 캠페인)를 호출한다. 그 결과를 이 키로 캐시(staleTime)해 네비게이션 안에서 재사용하고,
 * 워크스페이스 변경·플랜/캠페인 갱신 시 `invalidateQueries`로 새로 읽는다.
 *
 * ## 규약
 * - 키 모양은 `[도메인, 조회이름, …인자를 «값으로» 펼친 것]` — 객체를 통째로 넣지 않는다.
 * - 인자는 **원시값 한 줄**로 펼친다(`moment.ts` 의 `searchQuerySignature` 규약과 같은 자리).
 * - `*Root()` 는 `invalidateQueries`/`resetQueries` 대상용 접두 키다.
 * - ⛔ 위 `checkedList` 계약(`getQueryClient()` 로 auth 밖에서 닿는 자리)은 건드리지 마라.
 *
 * ## ⚠️ 결제 이력을 워크스페이스 도메인에 둔 이유
 * `getSubscription(workspaceId)` 는 **PaymentHistory 를 워크스페이스 기준으로** 읽는다 —
 * 사용자 기준이 아니다(`use-account-subscription` 머리말: 같은 워크스페이스 구성원이면
 * 오너의 결제 내역이 보인다). 그래서 `/account/subscription` 과
 * 설정 「구독·결제」(`?tab=subscribe`)가 **같은 조회**를 하고, 여기서 **같은 키를 나눠 쓴다**.
 */
export const workspaceKeys = {
  all: ['workspace'] as const,

  checkedListRoot: () => [...workspaceKeys.all, 'checked-list'] as const,
  /** getCheckedWorkspaceList 결과({ workspaces, isNew }) — uid 기준 */
  checkedList: (uid: string | null | undefined) =>
    [...workspaceKeys.checkedListRoot(), uid ?? ''] as const,

  /**
   * 어드민 전체 워크스페이스 목록 — **스위처를 열 때만** 읽는다.
   *
   * `checkedList` 와 키를 나눈다: 「내 워크스페이스」와 「어드민이 둘러보는 전체」는 성격이
   * 다른 질문이고 수명도 다르다 — 한 키에 뭉치면 staleTime 을 길게 줄 수 없고, 스위처를
   * 열지 않는 접속까지 전량을 싣게 된다. uid 는 키에 넣지 않는다 — 어드민 누구에게나 같은 목록이다.
   */
  adminList: () => [...workspaceKeys.all, 'admin-list'] as const,

  // ── 시딩(인플루언서) 캠페인 목록 + 워크스페이스 문서 ──────────────────────────
  /**
   * ⚠️ 캠페인 목록과 워크스페이스 문서를 **한 캐시 항목**으로 묶는다.
   * `Promise.all` 로 둘을 같이 읽어 **한 tick 에 반영**해야 하는 자리다 — 두 왕복이 쿼리 하나다.
   */
  influencerCampaignsRoot: () => [...workspaceKeys.all, 'influencer-campaigns'] as const,
  influencerCampaigns: (workspaceId: string | null | undefined) =>
    [...workspaceKeys.influencerCampaignsRoot(), workspaceId ?? ''] as const,

  // ⚠️ 캠페인 컬렉션은 `brandKeys.dataStatsCampaigns`(옵션 목록)·`notificationCampaigns`(알림)도 읽는다 —
  //    응답 모양·인자가 달라 키를 안 합친다(brand.ts 머리말). 합치면 캐시 공유라는 동작 변경이다(A541).

  // ── 결제 이력 (두 화면이 나눠 쓴다 — 머리말 참조) ──────────────────────────────
  /** `getSubscription(workspaceId)` → `payment_status === 'success'` 만 남긴 목록 */
  paymentHistoryRoot: () => [...workspaceKeys.all, 'payment-history'] as const,
  paymentHistory: (workspaceId: string | null | undefined) =>
    [...workspaceKeys.paymentHistoryRoot(), workspaceId ?? ''] as const,
  /** 첫 결제 보너스를 이미 받았는가 — 같은 조회(`payment.history.get`)를 상태 무관하게 본 답(boolean) */
  paymentBonusUsed: (workspaceId: string | null | undefined) =>
    [...workspaceKeys.paymentHistoryRoot(), 'bonus-used', workspaceId ?? ''] as const,

  // ── 알림 화면 ───────────────────────────────────────────────────────────────
  /**
   * 알림 설정 화면이 워크스페이스 문서 하나에서 «한꺼번에» 꺼내는 것 —
   * 문서 · 멤버 id 목록 · 브랜드 멘션 기본값 · 이메일 수신인 둘 · 슬랙 연결 여부.
   * 한 왕복에서 나오는 것이라 캐시 항목도 하나다.
   */
  notificationSetupRoot: () => [...workspaceKeys.all, 'notification-setup'] as const,
  notificationSetup: (workspaceId: string | null | undefined) =>
    [...workspaceKeys.notificationSetupRoot(), workspaceId ?? ''] as const,

  /**
   * 알림 화면의 캠페인 목록 — 저장 문서에 알림 기본값(Daily ON · Weekly OFF · 24h)을 채워 넣은 것.
   * @param slackWebhookUrl 워크스페이스의 슬랙 웹훅. 웹훅이 바뀌면 캠페인별
   *   `messageSlackRealTime` 판정이 달라져 목록을 다시 읽어야 하므로 **키의 일부**다.
   */
  notificationCampaignsRoot: () => [...workspaceKeys.all, 'notification-campaigns'] as const,
  notificationCampaigns: (
    workspaceId: string | null | undefined,
    slackWebhookUrl: string,
  ) => [...workspaceKeys.notificationCampaignsRoot(), workspaceId ?? '', slackWebhookUrl] as const,

  /**
   * 브랜드 설정 지난 버전 목록(WorkspaceProfiles/{ws}/brandEvalSettingHistory) — 변경 이력 서브탭.
   * @param currentHistoryKey 지금 brandSetting 의 key. **회전하면 키가 바뀌어** 새 아카이브를
   *   다시 읽는다 — 실시간 구독(liveWorkspace)이 무효화 신호를 겸하는 모양이다.
   */
  brandSettingVersionsRoot: () => [...workspaceKeys.all, 'brand-setting-versions'] as const,
  brandSettingVersions: (workspaceId: string | null | undefined, currentHistoryKey: string) =>
    [...workspaceKeys.brandSettingVersionsRoot(), workspaceId ?? '', currentHistoryKey] as const,

  /**
   * 이번 달 MCP 질문 수(`credit.read`) — 설정 「연결」 › MCP 와 홈 머리 MCP 칩이 나눠 쓴다.
   * 워크스페이스는 서버가 uid 로 정한다 — 키의 워크스페이스는 바꿔 들어갔을 때 다시 읽게 하는 몫이다.
   */
  mcpQuota: (workspaceId: string | null | undefined) => [...workspaceKeys.all, 'mcp-quota', workspaceId ?? ''] as const,
};
